import {
  isCollectionItem, isItemWithValue, isLightTable, isMultiUnitValuesItem, isValueEmpty,
} from 'core/api/api.typesGuards';
import type * as I from 'core/api/coreApiClient';
import type { PageSectionState, SectionIdToSectionState } from 'core/redux/reducers/page/pageReducer.types';
import { hasRowAnyValueSet, metaInfoInfos } from 'core/utilities/table/table';
import isEqual from 'react-fast-compare';

const getEmptySectionCopy = (section: I.Section): I.Section => ({
  id: section.id,
  type: section.type,
  expanded: section.expanded,
  metaInfo: section.metaInfo,
  propertyValues: [],
  commands: [],
  sections: section.sections,
});

export const flattenSections = (sections: I.Section[]): I.Section[] => (
  sections.reduce((acc, section) => {
    let newAcc: I.Section[] = [...acc];
    newAcc.push(section);
    newAcc = newAcc.concat(flattenSections(section.sections));
    return newAcc;
  }, [] as I.Section[])
);

type SectionsWithParentId = [I.Section, string?][];

export const flattenSectionsWithParentId = (sections: I.Section[], parentId?: string): SectionsWithParentId => (
  sections.reduce((acc, section) => {
    let newAcc: SectionsWithParentId = [...acc];
    newAcc.push([section, parentId]);
    newAcc = newAcc.concat(flattenSectionsWithParentId(section.sections, section.id));
    return newAcc;
  }, [] as SectionsWithParentId)
);

export const findSection = (sections: I.Section[], sectionId: string) => {
  const flattenedSections = flattenSections(sections);
  return flattenedSections.find((s) => s.id === sectionId);
};

/**
 * Find direct parents from a given section
 * @returns array of sections where the first element is the given section and the last element the root section
 */
export const findSectionParents = (sections: I.Section[], sectionId: string) => {
  const flattenedSections = flattenSectionsWithParentId(sections);

  return flattenedSections.reduce((acc, [section, parentId]) => {
    let newAcc = [...acc];
    if (section.id === sectionId) {
      newAcc.push(section);
      if (parentId) {
        newAcc = newAcc.concat(findSectionParents(sections, parentId));
      }
    }
    return newAcc;
  }, [] as I.Section[]);
};

export const findSectionState = (
  sections: SectionIdToSectionState, sectionId: string,
): PageSectionState | undefined => {
  let found: PageSectionState | undefined;

  Object.values(sections).forEach((section) => {
    if (!found && section.id === sectionId) {
      found = section;
    }
    if (!found) {
      found = findSectionState(section.sections, sectionId);
    }
  });

  return found;
};

export const replaceSectionState = (
  sections: SectionIdToSectionState, updatedSection: PageSectionState,
): SectionIdToSectionState => (
  Object.values(sections).reduce((acc, section) => {
    const newAcc: SectionIdToSectionState = { ...acc };

    const selectedSection = section.id === updatedSection.id ? updatedSection : section;

    newAcc[selectedSection.id] = {
      ...selectedSection,
      sections: replaceSectionState(selectedSection.sections, updatedSection),
    };
    return newAcc;
  }, {} as SectionIdToSectionState)
);

export const createSectionsWithoutPropertyValues = (sections: I.Section[]): I.Section[] => (
  sections.map((section) => ({
    ...(getEmptySectionCopy(section)),
    sections: [...createSectionsWithoutPropertyValues(section.sections)],
  }))
);

export const haveSectionsBeenEdited = (sections: I.Section[]): boolean => {
  const flattenedSections = flattenSections(sections);
  return flattenedSections.some((s) => s.propertyValues.length > 0 || s.lightTable);
};

const updateSectionSection = (section: I.Section, updatedSection: I.Section): I.Section => ({
  ...section,
  sections: section.sections.map((prevSection) => (
    updateSectionSection(prevSection.id === updatedSection.id ? updatedSection : prevSection, updatedSection)
  )),
});

export const updateSectionsSection = (sections: I.Section[], updatedSection: I.Section): I.Section[] => (
  sections.map((prevSection) => (
    updateSectionSection(prevSection.id === updatedSection.id ? updatedSection : prevSection, updatedSection)
  ))
);

/**
 * Update given sections metaInfo with updated metaInfo.
 * Trigger property call might influence another property info, this is used to refresh edited sections metaInfo.
 */
export const updateSectionsInfo = (sections: I.Section[], updatedSections: I.Section[]): I.Section[] => (
  sections.map((section) => {
    const updatedSection = findSection(updatedSections, section.id);
    return {
      ...section,
      metaInfo: updatedSection?.metaInfo,
      sections: updateSectionsInfo(section.sections, updatedSection?.sections || []),
    };
  })
);

/**
 * Iterate through page sections tree to find an replace a section with the updated one
 *
 * @param page
 * @param updatedSection
 */
export const updatePageSection = (page: I.Page, updatedSection: I.Section): I.Page => ({
  ...page,
  sections: updateSectionsSection(page.sections, updatedSection),
});

export const isValueATriggerProperty = (section: I.Section, data: I.Value | I.LightTable) => {
  if (!section.metaInfo || isLightTable(data)) return false;
  const infos = metaInfoInfos(section.metaInfo);
  const info = infos.find((i) => i.id === data.id);
  return (info && info.triggerProperty);
};

/**
 * Look up through all table values to find one marked as triggerCause
 */
export const hasLightTableATriggerCause = (data: I.LightTable) => {
  const allValues = data.rows.reduce<I.Value[]>((acc, row) => {
    let newAcc: I.Value[] = [...acc];
    newAcc = newAcc.concat(row.values);
    return newAcc;
  }, []);

  return allValues.some((v) => v.triggerCause);
};

const tryFlattenItem = (item: I.Item | undefined) => {
  if (!item || !isCollectionItem(item)) {
    return item;
  }

  const subItems: I.Item[] = item.items;
  if (!subItems) {
    return undefined;
  }
  if (subItems.length === 1) {
    return subItems[0];
  }

  return item;
};

const isDirtyItem = (
  newValue: I.Value | undefined, newItem: I.Item | undefined, baseItem: I.Item | undefined,
): boolean => {
  if (!newItem && !baseItem) {
    return false;
  }

  const withBefore = baseItem;
  const noBefore = !withBefore;

  if (withBefore && !isMultiUnitValuesItem(baseItem) && isMultiUnitValuesItem(newItem) && !isValueEmpty(newValue)) {
    return !isEqual((baseItem as I.MultiUnitsItem).value, (newItem).value)
      || !isEqual((baseItem as I.MultiUnitsItem).unitId, (newItem).unitId);
  }
  if (withBefore && isItemWithValue(baseItem) && isItemWithValue(newItem)) {
    return !isEqual((baseItem as I.BaseItem).value, (newItem as I.BaseItem).value);
  }
  if (withBefore && isEqual(baseItem, newItem)) {
    return false;
  }
  if (noBefore && isValueEmpty(newValue)) {
    return false;
  }

  return true;
};

const isDirtyValue = (newValue: I.Value, baseValue: I.Value | undefined): boolean => {
  if (newValue.triggerCause) {
    return true;
  }

  if (!newValue && !baseValue) {
    return false;
  }

  return isDirtyItem(
    newValue,
    tryFlattenItem(newValue ? newValue.item : undefined),
    tryFlattenItem(baseValue ? baseValue.item : undefined),
  );
};

const isDirtyTable = (newTable: I.LightTable, baseTable: I.LightTable | undefined): boolean => {
  if (!newTable && !baseTable) {
    return false;
  }

  const newRows: I.Row[] = newTable ? newTable.rows.filter((row) => hasRowAnyValueSet(row)) : [];
  const baseRows: I.Row[] = baseTable ? baseTable.rows : [];
  if (newRows.length !== baseRows.length) {
    return true;
  }

  return newRows.some((newRow, i) => {
    const baseRow = baseRows.find((row) => row.id === newRow.id) || baseRows[i];
    return newRow.values.some((newValue) => {
      const baseValue = baseRow.values.find((v) => v.id === newValue.id);
      return isDirtyValue(newValue, baseValue);
    });
  });
};

export const updateEditedSections = (
  editedSections: I.Section[],
  editedSectionId: string,
  editedData: I.Value | I.LightTable,
  sectionsBaseline: I.Section[],
) => {
  let newEditedSections: I.Section[] = [...editedSections];

  const editedSection = findSection(newEditedSections, editedSectionId);

  const sectionBaseline = findSection(sectionsBaseline, editedSectionId);

  if (editedSection) {
    // The section was already edited, add the value to the list
    const newEditedSection: I.Section = getEmptySectionCopy(editedSection);

    if (isLightTable(editedData)) {
      const isDirty = sectionBaseline
        ? isDirtyTable(editedData, sectionBaseline.lightTable)
        : true;

      newEditedSection.lightTable = isDirty ? editedData : undefined;
    } else {
      newEditedSection.propertyValues = editedSection.propertyValues.filter((pv) => pv.id !== editedData.id);

      const isDirty = sectionBaseline
        ? isDirtyValue(editedData, sectionBaseline.propertyValues.find((pv) => pv.id === editedData.id))
        : true;

      if (isDirty) {
        newEditedSection.propertyValues.push(editedData);
      }
    }

    newEditedSections = updateSectionsSection(newEditedSections, newEditedSection);
  }

  return newEditedSections;
};

export const updateSectionValue = (section: I.Section, data: I.Value | I.LightTable) => {
  const newSection = { ...section };

  if (isLightTable(data)) {
    newSection.lightTable = data;
  } else {
    const value = data;
    const propertyValues = [...section.propertyValues];
    let newPropertyValues = [];

    const existingPropertyValue = propertyValues.find((pv) => pv.id === value.id);

    if (existingPropertyValue) {
      newPropertyValues = propertyValues.map((pv) => (
        pv.id === value.id ? value : pv
      ));
    } else {
      newPropertyValues = [...propertyValues];
      newPropertyValues.push(value);
    }

    newSection.propertyValues = newPropertyValues;
  }

  return newSection;
};

export const filterOutUneditableValue = (sections: I.Section[]) => sections.map((section) => {
  const newSection: I.Section = { ...section };
  const infos: I.Info[] = newSection.metaInfo ? metaInfoInfos(newSection.metaInfo) : [];

  newSection.propertyValues = newSection.propertyValues.filter((value) => {
    const info = infos.find((i) => i.id === value.id);
    return info !== undefined && info.editable !== false;
  });

  newSection.sections = filterOutUneditableValue(newSection.sections);

  return newSection;
});

export const filterOutHiddenSections = (sections: I.Section[], visibleSections: I.Section[]): I.Section[] => (
  sections
    .filter((section) => findSection(visibleSections, section.id))
    .map((section) => ({ ...section, sections: filterOutHiddenSections(section.sections, visibleSections) }))
);

/**
 * The following types of sections are not editable:
 * - any type without metaInfo are not editable
 * - type LightTable where lightable.editable is not true are not editable
 * - ensure the logic above doesn't apply if a section has editable sub-section(s)
 */
export const filterOutNonEditableSections = (sections: I.Section[]): I.Section[] => (
  sections
    .map((section) => ({ ...section, sections: filterOutNonEditableSections(section.sections) }))
    .filter((section) => (
      section.sections.length > 0
      || (section.metaInfo && metaInfoInfos(section.metaInfo).length > 0 && !section.lightTable)
      || section.lightTable?.editable
    ))
);

export const filterOutNonEditedSections = (sections: I.Section[]): I.Section[] => (
  filterOutNonEditableSections(sections)
    .filter((section) => haveSectionsBeenEdited([section]))
    .map((section) => ({ ...section, sections: filterOutNonEditedSections(section.sections) }))
);

export const valueAsBoolean = (value?: boolean | string) => {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return undefined;
};

/**
 * Update sections sub-sections using newly provided newSections.
 * Sub-sections are created without properly values as they are part of the page sections state that
 * is only updated when the user update a value, later sent to the backend.
 */
export function updateSubSections(sections: I.Section[], newSections: I.Section[]) {
  return sections.map((section) => {
    const updatedSection: I.Section = { ...section };
    const newSectionSubSections = findSection(newSections, section.id);

    if (!newSectionSubSections) return updatedSection;

    // New sub-sections
    const updatedSectionSubSectionIds = updatedSection.sections.map(({ id }) => id);
    newSectionSubSections.sections.forEach((subSection) => {
      if (!updatedSectionSubSectionIds.includes(subSection.id)) {
        updatedSection.sections.push(...(createSectionsWithoutPropertyValues([subSection])));
      }
    });

    // Removed sub-sections
    const newSectionSubSectionsIds = newSectionSubSections.sections.map(({ id }) => id);
    updatedSection.sections = updatedSection.sections.filter((subSection) => (
      newSectionSubSectionsIds.includes(subSection.id)
    ));

    return updatedSection;
  });
}

export function removeTriggerCauseFromValues(sections: I.Section[]) {
  return sections.map((section) => {
    const newSection: I.Section = {
      ...section,
      propertyValues: section.propertyValues.map((value) => {
        const newValue: I.Value = { ...value };
        delete newValue.triggerCause;
        return newValue;
      }),
      lightTable: section.lightTable ? {
        ...section.lightTable,
        rows: section.lightTable.rows.map((row) => ({
          ...row,
          values: row.values.map((value) => {
            const newValue: I.Value = { ...value };
            delete newValue.triggerCause;
            return newValue;
          }),
        })),
      } : undefined,
      sections: removeTriggerCauseFromValues(section.sections),
    };

    return newSection;
  });
}

/**
 * Mark values that have been changed within the page response as changed (pushed to editedSections)
 * so they are been sent in follow up requests to the backend.
 *
 * Only run the check against sections that exists in the baseline.
 * For example a trigger property might have added a new section. We don't check if data has been changed by the backend within that section.
 *
 * sectionsBaseline: contains the INITIAL data that response sections should be compared to,
 *                   if something has been changed it should be added to editedSections
 * responseSections: might contain some new changed data that should be ported to editedSections
 * editedSections:   contains ONLY edited/changed data
 */
export function markAdditionalEditedSectionsValues(
  sectionsBaseline: I.Section[],
  responseSections: I.Section[],
  editedSections: I.Section[],
): I.Section[] {
  let newEditedSections: I.Section[] = [...editedSections];

  sectionsBaseline.forEach((sectionBaseline) => {
    const responseSection = findSection(responseSections, sectionBaseline.id);
    let newEditedSection = findSection(newEditedSections, sectionBaseline.id) as I.Section;

    if (responseSection && sectionBaseline.metaInfo) {
      metaInfoInfos(sectionBaseline.metaInfo).forEach((sectionBaselineInfo) => {
        const sectionBaselineValue = sectionBaseline.propertyValues.find((v) => v.id === sectionBaselineInfo.id);
        const responseSectionValue = responseSection.propertyValues.find((v) => v.id === sectionBaselineInfo.id);
        const editedSectionValue = newEditedSection.propertyValues.find((v) => v.id === sectionBaselineInfo.id);

         
        const isDirty = (
          editedSectionValue // if the value was previously changed it's dirty anyway and editedSections should get the latest
          || (responseSectionValue && !sectionBaselineValue)
          || (!responseSectionValue && sectionBaselineValue)
        )
          ? true
          : (responseSectionValue ? isDirtyValue(responseSectionValue, sectionBaselineValue) : false);

        if (isDirty) {
          const newPropertyValues = newEditedSection.propertyValues.filter((v) => v.id !== sectionBaselineInfo.id);
          if (responseSectionValue) newPropertyValues.push(responseSectionValue);
          newEditedSection = {
            ...newEditedSection,
            propertyValues: newPropertyValues,
          };
          newEditedSections = updateSectionsSection(newEditedSections, newEditedSection);
        }
      });
    }
  });

  return newEditedSections;
}
